import { Matrix, Point } from '../math';
import { FULL_CIRCLE_ANGLE } from '../constants';
/**
 * Represents a rotation transformation with angle and optional center point.
 * Provides methods to convert to matrix form and create inverse transformations.
 */
export class Rotation {
    /**
     * Creates a new Rotation transformation.
     * @param angle The rotation angle in radians
     * @param x The x coordinate of the rotation center (optional)
     * @param y The y coordinate of the rotation center (optional)
     */
    constructor(angle, x, y) {
        this.x = x || 0;
        this.y = y || 0;
        this.angle = angle;
    }
    /**
     * Returns a string representation of the rotation transformation.
     * @returns String representation in CSS transform format
     */
    toString() {
        if (this.x && this.y) {
            return `rotate(${this.angle},${this.x},${this.y})`;
        }
        else {
            return `rotate(${this.angle})`;
        }
    }
    /**
     * Converts the rotation transformation to a matrix representation.
     * @returns A matrix representing the rotation transformation
     */
    toMatrix() {
        return Matrix.rotation(this.angle, this.x, this.y); // T*R*T^-1
    }
    /**
     * Gets the center point of the rotation.
     * @returns A Point representing the rotation center
     */
    center() {
        return new Point(this.x, this.y);
    }
    /**
     * Creates an inverted version of this rotation transformation.
     * @returns A new Rotation with inverted angle
     */
    invert() {
        return new Rotation(FULL_CIRCLE_ANGLE - this.angle, this.x, this.y);
    }
    /**
     * Creates a new Rotation instance from an existing rotation.
     * @param rotation The rotation to copy
     * @returns A new Rotation instance
     */
    static create(rotation) {
        return new Rotation(rotation.angle, rotation.x, rotation.y);
    }
    /**
     * Parses a rotation from a string representation.
     * @param str The string to parse
     * @returns A new Rotation instance
     */
    static parse(str) {
        const values = str.slice(1, str.length - 1).split(',');
        const angle = parseFloat(values[0]);
        const x = parseFloat(values[1]);
        const y = parseFloat(values[2]);
        return new Rotation(angle, x, y);
    }
}
/** A static instance representing zero rotation. */
Rotation.ZERO = new Rotation(0);
